﻿import { useMemo, useState } from 'react';
import { Alert, Card, Radio, Select, Space, Spin, Typography } from 'antd';
import RankingView from './RankingView';
import PivotControls from './PivotControls';
import PivotTable from './PivotTable';
import SavedViews from './SavedViews';
import ExportPanel from './ExportPanel';
import { useAnalyticsRanking } from './useAnalyticsRanking';
import { useAnalyticsQuery } from './useAnalyticsQuery';
import type { AnalyticsDimension, AnalyticsQuestion, AnalyticsSort, QueryDefinition, VersionSummary } from '../../../types/survey-analytics.types';

const { Text } = Typography;

export interface AnalyticsExplorerProps {
  surveyId: string;
  versions?: VersionSummary[];
  questions?: AnalyticsQuestion[];
  initialVersionId?: string;
  initialQuestionId?: string;
  loading?: boolean;
  error?: Error | null;
}

export function AnalyticsExplorer({
  surveyId,
  versions = [],
  questions = [],
  initialVersionId,
  initialQuestionId,
  loading = false,
  error = null,
}: AnalyticsExplorerProps) {
  const [versionId, setVersionId] = useState(initialVersionId ?? versions[0]?.versionId ?? '');
  const [questionId, setQuestionId] = useState(initialQuestionId ?? questions[0]?.questionId ?? '');
  const [search, setSearch] = useState('');
  const [sort, setSort] = useState<AnalyticsSort>('COUNT_DESC');
  const [cursor, setCursor] = useState<string | undefined>(undefined);
  const [includeZero, setIncludeZero] = useState(false);
  const [mode, setMode] = useState<'ranking' | 'pivot'>('ranking');
  const [pivotDefinition, setPivotDefinition] = useState<QueryDefinition | null>(null);

  const selectedQuestion = useMemo(
    () => questions.find((question) => question.questionId === questionId),
    [questions, questionId]
  );

  const ranking = useAnalyticsRanking({
    surveyId,
    versionId,
    questionId,
    search,
    sort,
    includeZero,
    cursor,
    pageSize: 20,
    enabled: Boolean(versionId && questionId && mode === 'ranking'),
  });

  const basePivotDefinition: QueryDefinition = pivotDefinition ?? {    questionId,
    mode: 'PIVOT',
    dimension: 'gender' as AnalyticsDimension,
    measures: selectedQuestion?.type === 'RATING' ? ['MEAN', 'VALID_BASE'] : ['COUNT', 'PERCENT_WITHIN_GROUP'],
    pageSize: 20,
    transpose: false,
    filters: {},
  };

  const pivot = useAnalyticsQuery({
    surveyId,
    versionId,
    definition: mode === 'pivot' ? basePivotDefinition : null,
    enabled: Boolean(versionId && questionId && mode === 'pivot'),
  });

  const resetQuery = () => {
    setSearch('');
    setCursor(undefined);
    setSort('COUNT_DESC');
    setIncludeZero(false);
    setPivotDefinition(null);
  };

  if (loading) {
    return <Spin />;
  }

  if (error) {
    return <Alert type="error" message="NÃ£o foi possÃ­vel carregar o explorador" description={error.message} />;
  }

  if (versions.length === 0 || questions.length === 0) {
    return <Alert type="info" message="Nenhuma versÃ£o ou pergunta analÃ­tica disponÃ­vel." />;
  }

  return (
    <Card title="Explorar anÃ¡lise">
      <Space direction="vertical" size="middle" style={{ width: '100%' }}>
        <Space wrap>
          <Select
            aria-label="VersÃ£o"
            value={versionId}
            style={{ minWidth: 260 }}
            onChange={(value) => {
              setVersionId(value);
              resetQuery();
            }}
            options={versions.map((version) => ({
              value: version.versionId,
              label: `${version.kind === 'PREVIEW' ? 'PrÃ©via' : 'Final'} Â· ${version.status}`,
            }))}
          />
          <Select
            aria-label="Pergunta"
            value={questionId}
            style={{ minWidth: 280 }}
            onChange={(value) => {
              setQuestionId(value);
              resetQuery();
            }}
            options={questions.map((question) => ({ value: question.questionId, label: question.title }))}
          />
          {selectedQuestion && <Text type="secondary">{selectedQuestion.optionCount} opÃ§Ãµes</Text>}
        </Space>

                <Radio.Group aria-label="Tipo de visualização" value={mode} onChange={(event) => setMode(event.target.value)}>
          <Radio.Button value="ranking">Ranking</Radio.Button>
          <Radio.Button value="pivot" disabled={!selectedQuestion?.capabilities.pivot}>Pivot</Radio.Button>
        </Radio.Group>

        {mode === 'ranking' ? (
          <RankingView
            result={ranking.data}
            loading={ranking.isLoading || ranking.isFetching}
            error={ranking.error as Error | null}
            search={search}
            sort={sort}
            includeZero={includeZero}
            onSearchChange={(value) => {
              setSearch(value);
              setCursor(undefined);
            }}
            onSortChange={(value) => {
              setSort(value);
              setCursor(undefined);
            }}
            onIncludeZeroChange={(value) => {
              setIncludeZero(value);
              setCursor(undefined);
            }}
            onNextPage={(nextCursor) => setCursor(nextCursor)}
          />
        ) : (
          <>
            <PivotControls
              definition={basePivotDefinition}
              onChange={(definition) => setPivotDefinition({ ...definition, questionId })}
              onRun={() => setPivotDefinition({ ...basePivotDefinition, questionId })}
            />
            <PivotTable result={pivot.data} loading={pivot.isLoading || pivot.isFetching} error={pivot.error} />
            <SavedViews surveyId={surveyId} versionId={versionId} definition={basePivotDefinition} onOpen={(definition) => setPivotDefinition({ ...definition, questionId })} />
            <ExportPanel surveyId={surveyId} versionId={versionId} definition={basePivotDefinition} />
          </>
        )}
      </Space>
    </Card>
  );
}

export default AnalyticsExplorer;




